import { Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import React from 'react';

import { CustomTableSelectMaxRowsStyles } from '@/components/table/components/pagination/styles/CustomTablePagination.styles.ts';
import { CustomTableVariant } from '@/components/table/CustomTable.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

interface Props {
  count: number;
  isVisible?: boolean;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  onRowsPerPageChange: (event: SelectChangeEvent<number>) => void;
  page: number;
  pageSize: number;
  rowsPerPageLabel?: string;
  rowsPerPageOptions?: number[];
  size: CustomTableVariant;
}

export const CustomTablePagination: React.FC<Props> = ({
  count,
  isVisible = true,
  onPageChange,
  onRowsPerPageChange,
  page,
  pageSize,
  rowsPerPageLabel,
  rowsPerPageOptions = [25, 50, 100],
  size,
}) => {
  const { t } = useTranslationWithPrefix('components.table.content');

  if (!isVisible) {
    return null;
  }

  return (
    <Stack
      sx={{
        alignItems: 'center',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 1.5,
        justifyContent: 'space-between',
        marginTop: size === 'small' ? 0.5 : 2,
      }}
    >
      <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 2 }}>
        <FormControl
          sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row', gap: 0.75 }}
        >
          <Typography sx={{ color: themeColors.greyDark }} variant="f12Regular">
            {rowsPerPageLabel ?? t('maxRows')}:
          </Typography>
          <Select
            aria-label={rowsPerPageLabel ?? t('maxRows')}
            disableUnderline
            onChange={onRowsPerPageChange}
            sx={CustomTableSelectMaxRowsStyles({ size })}
            value={pageSize}
            variant="standard"
          >
            {rowsPerPageOptions.map(option => (
              <MenuItem key={option} value={option}>
                <Typography variant="f12Regular">{option}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography sx={{ color: themeColors.greyDark }} variant="f12Regular">
          {page * pageSize + 1}-{Math.min((page + 1) * pageSize, count)} {t('of')} {count}
        </Typography>
      </Stack>
      <Pagination
        count={Math.ceil(count / pageSize)}
        onChange={onPageChange}
        page={page + 1}
        showFirstButton
        showLastButton
        size={size}
      />
    </Stack>
  );
};
